import { initializeConnector } from "@web3-react/core";
import { EIP1193 } from "@web3-react/eip1193";
import {
  Context,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { staticConnectors, staticConnectorsMap } from "../connectors";

export interface Web3ConnectorsState {
  connectors: typeof staticConnectors;
  connectorsMap: typeof staticConnectorsMap;
}

const Web3ConnectorsContext: Context<Web3ConnectorsState> =
  createContext<Web3ConnectorsState>({} as Web3ConnectorsState);

interface Web3ConnectorsProviderProps {
  children: ReactNode;
}

/**
 * This context and hook are used because XDEFI provider is injected dynamically after page is loaded,
 * so we cannot statically load it from the window object like we did for others providers.
 * The hook can be used in any place where we need to activate XDEFI connector.
 */

export const Web3ConnectorsProvider = ({
  children,
}: Web3ConnectorsProviderProps) => {
  const [web3Connectors, setWeb3Connectors] =
    useState<typeof staticConnectors>(staticConnectors);
  const [web3ConnectorsMap, setWeb3ConnectorsMap] =
    useState<typeof staticConnectorsMap>(staticConnectorsMap);

  useEffect(() => {
    const setupXdefiConnector = () => {
      if (!window.xfi) {
        return false;
      }
      const [xdefi, hooks] = initializeConnector<EIP1193>(
        (actions) => new EIP1193({ provider: window.xfi.ethereum, actions }),
      );
      setWeb3Connectors((connectors) => [...connectors, [xdefi, hooks]]);
      setWeb3ConnectorsMap((connectorsMap) => ({ ...connectorsMap, xdefi }));
      return true;
    };
    let xdefiInterval: NodeJS.Timeout;
    if (!setupXdefiConnector()) {
      xdefiInterval = setInterval(() => {
        if (setupXdefiConnector()) {
          clearInterval(xdefiInterval);
        }
      }, 500);
    }

    return () => clearInterval(xdefiInterval);
  }, []);

  return (
    <Web3ConnectorsContext.Provider
      value={{ connectors: web3Connectors, connectorsMap: web3ConnectorsMap }}
    >
      {children}
    </Web3ConnectorsContext.Provider>
  );
};

export const useWeb3Connectors = (): Web3ConnectorsState => {
  return useContext(Web3ConnectorsContext);
};
